/**
 * The encounter's movement-legality seam — the *one* place the `blocked` /
 * `inBounds` / `moveCost` / `blocksEdge` context is composed from live encounter
 * state, shared by the authoritative `move_token` reducer (the wall) and the VTT
 * movement-range overlay (the affordance). Keeping both on this single helper is
 * what guarantees the overlay can never drift from the rule the server enforces
 * (`CLAUDE.md`: "UI mirrors the reducer's rules as affordances; it never
 * re-enforces them" — same helpers, surfaced twice).
 *
 * Everything here is keyed through the encounter's spatial port `world`
 * (`occupancyKeys`/`world.key`/`inBounds`), so it is square or hex without knowing
 * the grid shape.
 */
import {
  type MovementContext,
  type Placement,
  type Reachable,
  type World,
  anchorLegPath,
  anchorPathTo,
  cellRegion,
  movePathCost,
  occupancyKeys,
  reachableAnchors,
} from "@lancer-vtt/space";
import { err, ok, type Result } from "@lancer-vtt/shared";
import type { Cell, Combatant, CombatantKind, EncounterState, FlightHeading } from "./types";
import {
  DOWN_AND_OUT_STATUS,
  IMMOBILIZED_STATUS,
  JOCKEYED_STATUS,
  JOCKEYING_STATUS,
  PRONE_STATUS,
  PROWLING_STATUS,
  IGNORE_DIFFICULT_TERRAIN_STATUS,
  RIDDEN_STATUS,
  RIDING_STATUS,
  RODEO_RIDER_STATUS,
  RODEO_STATUS,
  TETHERED_STATUS,
  TOWING_STATUS,
  TETHER_RANGE,
  hasStatus,
  isCharacter,
  isMapAsset,
  isOpenDoor,
  isRooted,
  isStunned,
  sideOf,
} from "./types";
import {
  edgeBlocksFlight,
  edgeBlocksMovement,
  flightBlockingTerrainKeys,
  moveCost as terrainMoveCost,
  solidTerrainKeys,
} from "./terrain";
import { objectMoveCostCells } from "./objectMovementContributions";
import { endpointFitterIn, placementFitter } from "./placementFit";
import { worldFor } from "./world";
import { grappleDominantOf, grapplePartner } from "./grappleDominance";
import { encounterGridMode, tagCell } from "./types";

/** A local kind check, deliberately not shared with this file's other kind-gates. */
function isObjectKind(c: Combatant): boolean {
  return c.kind === "object" || c.kind === "deployable";
}

/**
 * The placed combatants (those with a `pos`) as grid-agnostic {@link Placement}s
 * for occupancy queries — anchor + Size, the unit `move_token` validates against.
 *
 * **The destroyed-object occupancy gate**: occupancy is otherwise automatic — every
 * placed combatant blocks, and objects are unconditional path-blockers while alive —
 * so a **destroyed** object/deployable is explicitly excluded here, or it would keep
 * blocking movement forever after being destroyed.
 *
 * **The passable-object gate**: a **live** object/deployable whose
 * `objectProfile.blocksMovement` isn't `true` is *also* excluded here — it exits
 * occupancy entirely, joining neither the `blocked` set this feeds (via `occupancyKeys`,
 * the no-mover fallback below) nor `noStop` (which only `movementContext`'s mover-present
 * branch below populates). This keeps the fallback path and `movementContext`'s
 * mover-present path agreeing on which objects block at all — the twin gate lives in that
 * branch too.
 *
 * **An open door is excluded the same way** — gated through {@link isOpenDoor} (never a
 * bare "Open" status check) so a door fully exits occupancy the instant it's opened,
 * mirroring the Destroyed precedent. (A shut door authors `blocksMovement: true`, so only
 * the explicit open-state check frees it — the passable-object gate above wouldn't.)
 *
 * Non-object kinds (mech/npc/pilot) are unaffected by any of these gates.
 */
export function placements(state: EncounterState): Placement<Cell>[] {
  const out: Placement<Cell>[] = [];
  for (const c of Object.values(state.combatants)) {
    if (!c.pos) continue;
    // Excluded from occupancy (any of): destroyed, an open door, or a live passable
    // object (`blocksMovement !== true`) — mirrored in `movementContext` below.
    if (isObjectKind(c) && (hasStatus(c.statuses, "Destroyed") || isOpenDoor(c) || c.base.objectProfile?.blocksMovement !== true)) continue;
    out.push({ id: c.id, anchor: c.pos, size: c.base.size });
  }
  return out;
}

/**
 * Character kinds (mech/npc/pilot/drone) are the ones the RAW Size/side pass-through
 * rule (Core p.62 OBSTRUCTION) applies to; object/deployable kinds are out of its
 * scope and stay unconditional path-blockers, owned by `objectProfile.blocksMovement`.
 *
 * **Deliberately LOCAL and unexported, and deliberately NOT the canonical
 * `isCharacter`** exported from `types.ts` — this file's occupancy classification is
 * its own independently-evolving lane: whether a kind blocks/engages/passes-through is
 * a movement-lane decision made explicitly here, not a silent ride on a shared-symbol
 * rename. `drone` was added on its own terms (a drone is a pass-through ally body, not
 * an object-like hard wall). Can be unified with the canonical predicate in a later tidy
 * if the two ever converge for good reason; they are NOT guaranteed to always agree.
 */
function isCharacterKind(kind: CombatantKind): boolean {
  return kind === "mech" || kind === "npc" || kind === "pilot" || kind === "drone";
}

/**
 * The **one** named classification function the RAW move-through rule (Core p.62
 * OBSTRUCTION) composes through — whether `occupant`'s cell is traversable-but-
 * not-stoppable (`"noStop"`) or fully solid (`"blocked"`) *relative to `mover`*.
 * Tiers, in the order they're checked:
 *
 * 1. **Object/deployable kinds reaching this function are unconditional `"blocked"`**
 *    — the Size/side gate below scopes to character kinds only ({@link isCharacterKind});
 *    a literal Size-gated reading would let a Size-2+ mech phase through a destructible
 *    wall section. **A live object/deployable that DOESN'T author
 *    `objectProfile.blocksMovement: true` never reaches this function at all** —
 *    {@link movementContext}'s caller filters it out before calling
 *    `classifyOccupant`, the same pre-filter tier as the destroyed-object gate. So
 *    this clause is reached only by a genuinely blocking object (Wall/Boulder) or a
 *    non-object character. **Trampling does NOT clear this tier** — see tier 1a.
 * 1a. **A Trampling mover clears every CHARACTER occupant, hostile or allied alike
 *    → `"noStop"`** ({@link TRAMPLING_STATUS}, Core p.297) — checked immediately after the
 *    object-blocked tier above (so an object/deployable is UNAFFECTED, still `"blocked"`)
 *    and ahead of every side/kind-specific tier below, since RAW grants no side carve-out
 *    ("passes through occupied spaces" names no hostile-only restriction, unlike
 *    Prowling's tier 2 below). Endpoint legality is untouched here — `footprintEndpointFits`
 *    excludes every `noStop` cell exactly as it does for Flying/Terraforming/Prowling, so a
 *    Trampling mover passes through but still cannot END its move on an occupied cell.
 * 2. **A Prowling mover passes through a HOSTILE character occupant** (Infiltrator's
 *    "pass freely through — but not end your turn in — enemy spaces") **→ `"noStop"`** —
 *    scoped to hostile occupants only, unlike Trampling's tier 1a above.
 * 3. **A pilot-kind occupant is `"noStop"` for any non-pilot mover, regardless of
 *    side or Size** — RAW's "mechs can always move through spaces occupied by
 *    unmounted humans." (Two pilot-kind combatants fall through to the ordinary
 *    side/Size rule below — this clause is specifically about a MECH/NPC treading
 *    past a human, not pilot-on-pilot.)
 * 4. **A co-location-paired occupant (Jockey OR allied-ride) is `"noStop"` for its
 *    own partner, in EITHER direction** — a jockeying rider and its carrier (or a
 *    consensual rider and its willing mount) deliberately SHARE a cell (RAW: "climb
 *    onto the mech, sharing its space"), which the side/Size rule below would otherwise
 *    classify as mutually `"blocked"` (a hostile-or-Size-blocked occupant) — trapping
 *    the rider in place with no legal path to "jump off as part of your movement"
 *    (RAW), and blocking the carrier's own move too. Scoped to the EXACT partner pair
 *    (`Jockeying`/`Jockeyed` OR `Riding`/`Ridden`'s `partner` pointer), not a blanket
 *    exemption — a co-located rider still normally obstructs (or is obstructed by)
 *    every OTHER hostile it isn't paired with.
 * 5. **Same side (never hostile) → `"noStop"`** — "allied characters never cause
 *    obstruction."
 * 6. **Hostile character, strictly smaller Size → `"noStop"`**; equal-or-greater
 *    Size → `"blocked"` — the core Size-gated obstruction rule.
 *
 * Terrain-solid cells never reach this function (they're unioned into `blocked`
 * directly by {@link movementContext}, unconditionally, independent of the mover) —
 * EXCEPT that union itself routes to `noStop` instead when the mover is Flying (see
 * that branch in {@link movementContext}).
 *
 * 0. **A Flying mover clears EVERY occupant except a full-height object, checked first, ahead of
 *    every tier listed above (including the unconditional-`"blocked"` object/deployable clause,
 *    tier 1).** RAW p.63: FLIGHT "ignore[s] obstructions as long as it is physically possible to
 *    fly over/around them" — no Size/side/kind carve-out, so a flyer passes over a hostile of
 *    any Size, an ally, a pilot, or a genuinely-blocking object/deployable alike (still
 *    endpoint-blocked by {@link "@lancer-vtt/space".footprintEndpointFits} — it just can't
 *    STOP on the occupied cell). **The one exception is an object whose profile sets
 *    `blocksFlight`** (a full-height Wall): flying over it is not physically possible, so it
 *    falls through to tier 1 and reads `"blocked"`, the object twin of the full-height terrain
 *    cell. A Terraforming mover shares tier 0 but has no such exception.
 *
 * **`flyingOverride`** — an OPTIONAL 3rd argument, ORed with the status check above rather
 * than replacing it: `movementContext` passes its own `isFlyingMover` (which already folds
 * in `opts.as === "flying"`) here so a per-walk flight override (a Lunge that flies without
 * a stamped status) clears occupants exactly like status-based Flying does. It defaults to
 * `false`, an OR no-op, so a 2-arg call site is unaffected.
 */
export function classifyOccupant(mover: Combatant, occupant: Combatant, flyingOverride = false): "blocked" | "noStop" {
  // **Terraforming joins Flying at tier 0**, ahead of the unconditional-`"blocked"`
  // object/deployable clause, for the same structural reason: a rig that drives THROUGH
  // whatever is standing there must clear objects (a boulder, a deployed cover section)
  // exactly as it clears a hostile mech, or the bulldoze sweep could never reach an object
  // at all. It is NOT flight — see
  // {@link TERRAFORMING_STATUS} for the deliberately narrower exemption set (no Prone immunity,
  // no difficult-terrain cost exemption). `"noStop"` is the answer for EVERY walk that classifies
  // the rig as the mover, which is what keeps a push, teleport, placement or effect-driven travel
  // of the rig from landing it on a unit. Only the rig's own `move_token` (and the overlay
  // mirrors of it) may end on an occupant, and that exemption is not granted here:
  // `movementContext`'s `bulldozeFollows` opt-in drops the rig's occupant cells from `noStop`
  // after this function has classified them. See {@link TERRAFORMING_STATUS} item 1.
  if (hasStatus(mover.statuses, TERRAFORMING_STATUS)) return "noStop";
  // Flying clears every occupant EXCEPT a full-height object (`objectProfile.blocksFlight`), the
  // object twin of the full-height terrain cell `movementContext` keeps `blocked` for a flyer:
  // RAW's own carve-out, "as long as it is physically possible" to fly over. That occupant falls
  // through to the object clause below and reads `"blocked"`.
  if ((hasStatus(mover.statuses, FLYING_STATUS) || flyingOverride) && !isFullHeightObject(occupant)) return "noStop";
  // An object/deployable is an obstruction for EVERYONE (RAW p.62-63) — checked ahead of Prowling
  // (below), never after it. Prowling grants passage through "enemy **spaces**" (Core p.99), which
  // is a statement about hostile *characters*; RAW prints no object carve-out, so a wall, a
  // boulder or a deployed cover section blocks a Prowler exactly as it blocks anyone else.
  // **Reversing this order breaks it in both directions**: the object clause becomes reachable
  // only for an occupant the mover reads as same-side, so a Prowler passes through every object
  // the sides happen to disagree on (with every object stamped `"npc"`, that is a PC Prowler
  // walking through walls) — and once a deployed object takes its deployer's side
  // (`SpawnObjectArgs.allegiance`), a HOSTILE Prowler walks through a player's own cover. The
  // fix belongs here, not in tuning who is on whose side. Only Flying (tier 0, above)
  // legitimately clears objects.
  if (!isCharacterKind(occupant.kind)) return "blocked";
  // Trampling (tier 1a, see this function's own doc + {@link TRAMPLING_STATUS}): every
  // CHARACTER occupant is `"noStop"` for a Trampling mover, hostile or allied alike — RAW
  // grants no side carve-out, unlike Prowling immediately below. Checked before Prowling/
  // pilot/colocation/side so a Trampling mover's path is never accidentally narrower than
  // "every character" for want of ordering; the result would be identical either way since
  // every tier below also resolves to `"noStop"` for a character it clears, but this keeps
  // the "no side restriction" fact visible as its own branch rather than an emergent
  // coincidence of the tiers beneath it.
  if (hasStatus(mover.statuses, TRAMPLING_STATUS)) return "noStop";
  // Trip's pass-through: only the tripped character's space, and still endpoint-blocked.
  if (mover.statuses.some((s) => s.name === TRIPPING_STATUS && s.partner === occupant.id)) return "noStop";
  // Prowling (the Infiltrator's "pass freely through — but not end your turn in — enemy
  // spaces"): a Prowling mover passes THROUGH a hostile occupant (never blocked), but
  // — unlike Flying's blanket pass-over — only hostile CHARACTER occupants, and still
  // endpoint-blocked (`footprintEndpointFits` excludes `noStop` cells), so "can pass through but
  // not end in" falls out for free. Same-side/pilot/colocation occupants are already `noStop` below.
  if (hasStatus(mover.statuses, PROWLING_STATUS) && sideOf(occupant) !== sideOf(mover)) return "noStop";
  if (occupant.kind === "pilot" && mover.kind !== "pilot") return "noStop";
  if (isColocationPartnerOf(mover, occupant) || isColocationPartnerOf(occupant, mover)) return "noStop";
  if (sideOf(occupant) === sideOf(mover)) return "noStop";
  return occupant.base.size < mover.base.size ? "noStop" : "blocked";
}

/**
 * An occupant whose profile is full-height (`objectProfile.blocksFlight`), solid even to a flyer.
 * Read by capability presence, not kind: only object/deployable combatants carry an `objectProfile`.
 */
function isFullHeightObject(occupant: Combatant): boolean {
  return occupant.base.objectProfile?.blocksFlight === true;
}

/**
 * The co-location marker *family* — every status name that stamps a `partner`-pointed
 * pairing meant to SHARE a cell. Jockey's `Jockeying`/`Jockeyed` (hostile attach) and
 * allied-riding's `Riding`/`Ridden` (consensual attach) and Black Thumb's rodeo are distinct
 * pairs reached by distinct intents, but all want the identical occupancy exemption below — new
 * pairs join this list, not a new branch.
 */
const COLOCATION_MARKER_STATUSES: readonly string[] = [JOCKEYING_STATUS, JOCKEYED_STATUS, RIDING_STATUS, RIDDEN_STATUS, RODEO_RIDER_STATUS, RODEO_STATUS];

/** `true` iff `a` carries a co-location marker (any pair in {@link COLOCATION_MARKER_STATUSES}) whose `partner` points at `b`. */
function isColocationPartnerOf(a: Combatant, b: Combatant): boolean {
  return a.statuses.some((s) => COLOCATION_MARKER_STATUSES.includes(s.name) && s.partner === b.id);
}

/**
 * The RAW **Disengaged** status name (Lancer Core, DISENGAGE) — a first-class
 * `StatusInstance`, landed by `@lancer-vtt/rules`'s `disengageSteps` (the Hide-template
 * `request_disengage` resolution) and read here so {@link movementContext} skips installing
 * `stopsAt` whenever the mover carries it — the same single seam the overlay and the reducer
 * share, so neither can drift from the other. Exported (via the package barrel) so the room's
 * reaction-gate guard (`backend/.../encounterRoom.ts`) reads the identical string.
 */
export const DISENGAGED_STATUS = "Disengaged";

/**
 * Blademaster's **Feint** (`t_duelist:2`): "when moving, you ignore engagement and don't provoke
 * reactions from your target until the start of your next turn." The duelist carries it, with
 * `partner` = the feinted character. {@link movementContext} reads it like {@link
 * DISENGAGED_STATUS} for engagement, which RAW leaves unqualified, so it covers all engagement.
 * Only the partner's reactions are suppressed ({@link feintedBy}).
 */
export const FEINTING_STATUS = "Feinting";

/** Does `mover`'s Feint name `watcherId`, so that `watcherId` may not react to `mover`'s movement? */
export function feintedBy(mover: Combatant, watcherId: string): boolean {
  return mover.statuses.some((s) => s.name === FEINTING_STATUS && s.partner === watcherId);
}

/**
 * Blademaster's **Trip** (`t_duelist:2`): "you may freely pass through their space until the end of
 * your current turn, although you can't end your turn in their space." The duelist carries it, with
 * `partner` = the tripped character, whose space {@link classifyOccupant} makes pass-through-only.
 */
export const TRIPPING_STATUS = "Tripping";

/**
 * The RAW **Flying** status name (Lancer Core p.63, FLIGHT) — a runtime `StatusInstance`
 * `movementContext` consults, exactly how {@link DISENGAGED_STATUS}/`PRONE_STATUS` are
 * read here. Applied/removed via the generic `apply_status`/`remove_status` reducer
 * commands (`lvtt encounter status add|remove --status Flying`) — there is no bespoke
 * "grant flight" command; content that grants flight (a frame trait, a hover NPC) just
 * applies this status by name. Defined here (not `types.ts`) because, like Disengaged, its
 * only real consumers are movement-specific: {@link classifyOccupant}/{@link
 * movementContext} below, and the `Flying` seed overlay entry's Prone-immunity scope
 * (`seedEntries.ts`) matches purely by NAME (`statusOverlayEntry`), so it doesn't need to
 * import this constant at all.
 *
 * Exemptions granted (RAW verbatim, "ignore obstructions as long as physically
 * possible" + Prone-immunity), all consulted below: every occupant AND solid-terrain
 * cell becomes `noStop` (pass over, still endpoint-blocked — {@link classifyOccupant}
 * + the terrain union in {@link movementContext}), edge walls are ignored (`blocksEdge`
 * suppressed) — each EXCEPT its full-height (`blocksFlight`) form, which stays solid to a
 * flyer: a full-height cell, edge, or object (`objectProfile.blocksFlight`), and Prone-immunity lands via the seed overlay's `immunity` scope, gated
 * at `status_add` emission (`statusBlockedByImmunity`) — NOT here.
 * **Difficult-terrain immunity (`moveCost` → 1) is a labelled INFERENCE, not RAW** — see
 * the comment on that branch below. **Engagement `stopsAt` is DELIBERATELY LEFT ON**
 * (FLIGHT claims no engagement exemption, unlike TELEPORTATION) — do not touch it here.
 */
export const FLYING_STATUS = "Flying";

/**
 * The **Terraforming** status — the runtime capability that turns a combatant into a
 * terraforming rig: it drives THROUGH what is in its way, and the cells its footprint sweeps
 * along the path are smoothed flat behind it (the sweep itself is `@lancer-vtt/rules`'
 * `bulldozeSweep`/`bulldozeInto`, not this file — this constant only owns the *movement* half).
 *
 * **Why a status and not a new command / a frozen base capability.** Same argument
 * {@link FLYING_STATUS}'s own doc makes: this is a runtime-attachable capability, so content
 * that grants it just applies it by name through the generic `apply_status`/`remove_status`
 * (`lvtt encounter status add --status Terraforming`). There is no bespoke "grant bulldozing"
 * command, no new `EncounterCommand` union member, and a GM can switch the rig's blade off
 * mid-fight by removing the status — which is exactly the "can this change mid-fight?" test
 * that picks the status lane over a spawn-frozen `base` cluster.
 *
 * **Exemptions granted — deliberately a SUBSET of Flying's**, all three consulted in
 * {@link movementContext}/{@link classifyOccupant} below:
 *   1. **Every occupant → `noStop`** ({@link classifyOccupant} tier 0, shared with Flying) — the
 *      rig drives through hostiles, allies, pilots, and genuinely-blocking objects alike.
 *      **On its own `move_token` it may also END on one** (RAW Core p.62, OBSTRUCTION: a character
 *      "can't end a movement in a space that is occupied by another character or object **unless
 *      specified**"; the Terraforming Treads text is the specification, reworded in rig-stop-in-occupied
 *      Phase 2). That half is scoped to the
 *      move path by {@link movementContext}'s `bulldozeFollows` opt-in, which drops the rig's
 *      occupant cells from `noStop`. Only `move_token` (via `placement.ts`'s mid-turn branch) and
 *      its overlay mirrors ({@link reachableForCombatant}, {@link movePathAnchors}) pass it,
 *      because only `move_token` is followed by the bulldoze sweep (`referee/moveFollowups.ts`
 *      `bulldozeInto`) that runs the occupant over. A push, drag, teleport, `place_token`, effect
 *      travel or first placement of the rig does not opt in and is endpoint-blocked on an occupant
 *      exactly as before. The reducer's `move_token` leaves the occupant overlapped; the sweep
 *      that follows runs it over (2d6 kinetic + knockback 2) and then, if it is still under the
 *      rig's final footprint, moves it to the nearest free space outside it, ties toward the cell
 *      away from the rig, with a warning and no move when there is none (`bulldoze.ts` ruling 4,
 *      developer-ruled 2026-09-24). Allies and objects are treated the same, except a **map asset**
 *      ({@link isMapAsset}): under the opt-in it is no obstacle at all, not even a move cost, and the
 *      sweep removes it rather than running it over (`projects/terraformer-clears-assets.md`,
 *      developer 2026-09-29).
 *   2. **Solid terrain → not an obstacle at all** (neither `blocked` nor `noStop`) — the rig
 *      grinds through a rock outcrop rather than steering around it, **and may END its move
 *      standing where one was**. **Including a `blocksFlight` full-height cell**, which is where
 *      this parts company with Flying: full-height is RAW's carve-out for a *flyer* ("couldn't
 *      move through a gap smaller than their mech"), a statement about clearance overhead. A
 *      ground rig demolishing at ground level has no such constraint.
 *
 *      The endpoint half is load-bearing and easy to get wrong. Routing rock into `noStop`
 *      instead lets the rig drive through a boulder but not park within Size 4 of one, which on
 *      a rubble-strewn map excludes nearly every anchor it could otherwise reach — and the
 *      symptom points somewhere else entirely: it reads at the table as "the rig is ignoring
 *      neither walls nor rough ground" even though both are already exempt, because the overlay
 *      is simply empty. The stop is legal because the bulldoze is a follow-up of the SAME move,
 *      so the sweep clears the destination footprint; judging a post-bulldoze position against
 *      pre-bulldoze terrain is the bug.
 *   3. **Edge walls ignored** (`blocksEdge` suppressed, full-height included, same reasoning) —
 *      without this, a wall could never enter the swept path and so could never be erased, and
 *      "edge terrain erased outright" would be unreachable for the exact obstacles it names.
 *
 * **NOT granted** (the subset half): no Prone immunity, and **no difficult-terrain cost
 * exemption** — Flying's flat-`1` `moveCost` short-circuit is a labelled inference about being
 * *above* the ground, which is the opposite of what a rig does. The rig pays full cost to grind
 * through rough ground, so a bulldoze is bounded by Speed like any other move. Terrain and
 * occupants differ in SCOPE, not in endpoint legality: terrain is exempt on every walk (2 above),
 * occupants only on a `move_token` (1 above), because only that move is followed by the sweep.
 */
export const TERRAFORMING_STATUS = "Terraforming";

/**
 * The **Trampling** status (the Cataphract's Trample Trait, Core p.297: "ignores engagement;
 * passes through (not stops in) occupied spaces") — a DELIBERATELY NARROWER sibling of
 * {@link TERRAFORMING_STATUS}, sharing its "beside TERRAFORMING_STATUS" placement because both
 * are runtime-attachable movement capabilities read the same way (`hasStatus`, applied via the
 * generic `apply_status`/`remove_status`, no bespoke command).
 *
 * **Exactly two exemptions, both consulted below — and NOTHING else Terraforming grants:**
 *   1. **Every CHARACTER occupant → `noStop`** ({@link classifyOccupant}'s own Trampling tier),
 *      hostile AND allied alike (RAW names no side restriction, unlike Prowling's hostile-only
 *      carve-out) — but **objects/deployables are UNCHANGED**, still unconditionally `"blocked"`
 *      (`classifyOccupant`'s object-kind tier runs first and Trampling never reaches it). RAW's
 *      "occupied spaces" names characters, not terrain-like props; a rock or a deployed cover
 *      section still stops a Trampling mover exactly as it stops anyone else.
 *   2. **Engagement is ignored** ({@link movementContext}'s `stopsAt`, the same mechanism
 *      {@link DISENGAGED_STATUS} uses) — RAW's own "ignores engagement."
 *
 * **NOT granted, unlike Terraforming:** no terrain/edge pass-through (`movementContext`'s solid-
 * terrain union and `blocksEdge` stay untouched for a Trampling mover — a wall or rough ground
 * obstructs it exactly like any other ground unit), no endpoint exemption on an occupied cell
 * (`footprintEndpointFits` still excludes every `noStop` cell, so "passes through but does not
 * stop in" falls out for free, identically to Prowling/Terraforming), and no damage/knockback of
 * its own here — the RAW "1/turn dealing damage to those characters" clause is a SEPARATE
 * engine link (`referee/moveFollowups.ts`'s `trampleInto`, a second sweep beside `bulldozeInto`
 * that never touches the rig's own profile) plus an authored trigger clause, not a movement-lane
 * concern at all. This constant only owns the two exemptions above.
 */
export const TRAMPLING_STATUS = "Trampling";

/**
 * The set of ids of **equal-or-greater-Size hostile** combatants whose footprint would be
 * adjacent to `mover`'s footprint if its anchor were `anchor` — the movement STOP's gate
 * (RAW Core p.62 ENGAGEMENT: "characters that become ENGAGED by targets of equal or
 * greater SIZE during the course of a movement stop moving immediately"). **POSITIONS-ONLY**:
 * this reads the same `sideOf`/`base.size` the engagement status recomputer
 * (`@lancer-vtt/rules`'s `spatialRecompute.ts` `isEngaged`) reads, but stays Size-gated where
 * that predicate is not — "Size matters twice, differently." It **never** reads the `Engaged`
 * status itself (stale after `place_token`/`forced_move`); this is a pure geometry query safe
 * to call for a hypothetical `anchor` the mover hasn't actually reached yet, which is exactly
 * how {@link engagementStopsAt} uses it — once per candidate edge during the reach flood/A*.
 */
export function engagersAt(state: EncounterState, mover: Combatant, anchor: Cell): Set<string> {
  const world = worldFor(state);
  const moverFootprint = cellRegion(world.footprint(anchor, mover.base.size).cells(), world.key);
  const mySide = sideOf(mover);
  const out = new Set<string>();
  for (const other of Object.values(state.combatants)) {
    if (other.id === mover.id || !other.pos) continue;
    // An object/deployable is NOT a character — engagement is a character-vs-character rule
    // (RAW Core p.62; objects are scoped to pure occupancy-blocking, out of the engagement
    // machine), so a wall/boulder (which folds to the "npc" side via `sideOf`) never ENGAGES a
    // passing mover. It already blocks by occupancy; it must not ALSO stop a mover moving
    // alongside it, and a Destroyed one contributes nothing at all. Same local-predicate
    // discipline as the rest of this file — no shared `isActor` import.
    //
    // A `drone` DOES reach this loop (`isCharacterKind` includes it) — it is a character, so it
    // can be a mid-move STOP. The Size gate two lines down still filters it against any Size-1+
    // mover (a drone is Size 1/2), so mechs/NPCs are unaffected; but a hostile Size-1/2 mover (a
    // pilot, or another drone) moving adjacent to a drone STOPS here, exactly as passing a
    // hostile pilot does (pilot-symmetric). This is deliberately STOP-WITHOUT-STATUS:
    // `engagersAt` only gates the movement stop — it is NOT read by `isEngaged`/`isEngagedWith`
    // (`spatialRecompute.ts`), which stay `isActor`-gated and so never grant the drone (or its
    // mover) the `Engaged` status from this encounter. A drone can stop a hostile's move but
    // never becomes — nor makes anyone else — "Engaged" by it. The codebase's "Size matters
    // twice, differently" split (this file's `engagersAt` vs. `isEngaged`'s unSized reading)
    // taken one step further.
    if (!isCharacterKind(other.kind)) continue;
    if (sideOf(other) === mySide) continue; // only a hostile engages.
    if (other.base.size < mover.base.size) continue; // the stop is equal-or-greater only.
    const otherFootprint = cellRegion(world.footprint(other.pos, other.base.size).cells(), world.key);
    if (world.regionDistance(moverFootprint, otherFootprint) === 1) out.add(other.id);
  }
  return out;
}

/**
 * The engagement movement-stop terminal-edge predicate — `MovementContext.stopsAt`
 * (`@lancer-vtt/space`), composed into {@link movementContext} for a mover that lacks
 * `Disengaged`. RAW: a mid-move stop is **per-NEW-hostile** — "you may make further move
 * actions as normal and moving into other adjacent spaces does not stop your movement"
 * once already Engaged with that hostile. Edge `from → to` stops
 * iff `to` gains an engager `from` didn't already have: `engagersAt(to) \ engagersAt(from)
 * ≠ ∅`. This makes circling an already-engaged hostile free, and sliding from one
 * hostile's ring into a second, different hostile's ring a fresh stop.
 */
function engagementStopsAt(state: EncounterState, mover: Combatant): (from: Cell, to: Cell) => boolean {
  return (from: Cell, to: Cell) => newEngagersOnStep(state, mover, { from, to }).length > 0;
}

/**
 * The hostiles that stop `mover` on the step `from → to`: `engagersAt(to) \ engagersAt(from)`,
 * sorted by id. The set {@link engagementStopsAt} tests for emptiness, exported so the move forecast
 * names who stops the move from the same difference. It does not check the Disengaged/Trampling
 * suppression; a caller asks `movementContext(...).stopsAt` first.
 */
export function newEngagersOnStep(state: EncounterState, mover: Combatant, step: { from: Cell; to: Cell }): string[] {
  const before = engagersAt(state, mover, step.from);
  return [...engagersAt(state, mover, step.to)].filter((id) => !before.has(id)).sort();
}

/**
 * Build the *one* movement context a `move_token` validates against — the shared
 * `blocked`/`noStop`/`inBounds`/`moveCost`/`blocksEdge`/`stopsAt` seam. Every OTHER
 * placed combatant is classified mover-relative through {@link classifyOccupant}:
 * same-side, a strictly-smaller hostile character, or a pilot-kind occupant (vs. a
 * non-pilot mover) lands in `noStop` (traversable, not stoppable); an equal-or-greater
 * hostile character or a genuinely-blocking object/deployable (`objectProfile.
 * blocksMovement: true`) lands in `blocked` (solid), same as terrain-solid cells
 * (unioned in unconditionally, independent of the mover). A **live** object/
 * deployable that does NOT author `blocksMovement: true` is excluded from this
 * classification altogether — it joins neither set, though it may still raise
 * `moveCost` (below). `exceptId` is the moving combatant (it never blocks/gates itself).
 *
 * **Missing mover ⇒ all-solid fallback** — if `exceptId` doesn't resolve to a live
 * combatant (a defensive/fixture case; several tests build contexts for ids not in
 * state), there is no mover to classify relative to, so every other placed
 * combatant is treated as solid.
 */
export function movementContext(
  world: World<Cell>,
  state: EncounterState,
  /**
   * The moving combatant's id — a bare string. OR (`CODE_STYLE.md`'s `max-params-3` gate is why
   * this rides the 3rd param rather than a 4th) `{ exceptId, as }`: a per-walk movement-type
   * override, ADDITIONAL to (never a replacement for) status-based flight detection —
   * `as === "flying"` OR the mover already carrying `FLYING_STATUS` both make this walk fly. This
   * is how a Lunge (`move_self_toward_target{as:"flying"}`) flies for the duration of ONE walk
   * without stamping `Flying` onto the mover (which would leak into the rest of the activation —
   * see the atom's own doc). A bare string is exactly `{ exceptId: <that string> }` with no
   * override.
   *
   * `bulldozeFollows: true` says this walk is the mover's own `move_token`, which the referee
   * follows with the bulldoze sweep. It only changes anything for a Terraforming mover: its
   * occupant cells leave `noStop`, so it may END on a character or object (see
   * {@link TERRAFORMING_STATUS} item 1). Every other caller omits it, so a pushed, teleported or
   * placed rig stays endpoint-blocked on an occupant.
   *
   * The same opt-in carries the grapple drag, which also follows only the mover's own
   * `move_token`: a dominant grappler's dragged partner is no obstacle and no engagement stop,
   * and an endpoint over it is legal only when the partner has a legal mirrored cell to be pushed
   * to ({@link draggedPartnerOf}, `projects/grapple-move-feel.md`).
   */
  exceptIdOrSpec: string | { exceptId: string; as?: "flying"; bulldozeFollows?: true },
): MovementContext<Cell> {
  const exceptId = typeof exceptIdOrSpec === "string" ? exceptIdOrSpec : exceptIdOrSpec.exceptId;
  const flightOverride = typeof exceptIdOrSpec === "string" ? undefined : exceptIdOrSpec.as;
  const bulldozeFollows = typeof exceptIdOrSpec !== "string" && exceptIdOrSpec.bulldozeFollows === true;
  const blocked = new Set<string>();
  const noStop = new Set<string>();
  const mover = state.combatants[exceptId];
  // Computed BEFORE the occupant loop so `classifyOccupant`'s per-walk override (below) can read
  // it — `flightOverride === "flying"` makes THIS walk fly the same as `FLYING_STATUS` would,
  // without requiring the status to be stamped on the mover.
  const isFlyingMover = (mover !== undefined && hasStatus(mover.statuses, FLYING_STATUS)) || flightOverride === "flying";
  // The rig's own obstruction exemption, read alongside (never folded into) `isFlyingMover` so
  // the two stay independently legible: a Terraforming mover gets Flying's occupant/terrain/edge
  // pass-through but NOT its Prone immunity or its difficult-terrain cost exemption, and it
  // clears full-height terrain/walls that stop a flyer.
  // See {@link TERRAFORMING_STATUS} for the full exemption ledger and why each half falls where it does.
  const isTerraformingMover = mover !== undefined && hasStatus(mover.statuses, TERRAFORMING_STATUS);
  // Bulwark Mods' "ignore difficult terrain": terrain and passable objects charge 1, the Prone
  // crawl floor still applies (see {@link IGNORE_DIFFICULT_TERRAIN_STATUS}).
  const ignoresDifficultTerrain = mover !== undefined && hasStatus(mover.statuses, IGNORE_DIFFICULT_TERRAIN_STATUS);
  // The rig's own move may end on an occupant (RAW p.62 "unless specified"; see
  // {@link TERRAFORMING_STATUS} item 1). `classifyOccupant` answers `"noStop"` for all of them,
  // so dropping that tier is the whole exemption.
  const mayStopOnOccupants = isTerraformingMover && bulldozeFollows;
  const dragged = bulldozeFollows && mover !== undefined ? draggedPartnerOf(state, mover) : undefined;
  if (!mover) {
    // No mover to classify relative to — fall back to the undifferentiated
    // occupancy index (everyone else is solid).
    for (const key of occupancyKeys(world, placements(state), exceptId)) blocked.add(key);
  } else {
    for (const other of Object.values(state.combatants)) {
      if (other.id === exceptId || !other.pos) continue;
      // The dominant grappler's dragged partner moves with it (the drag recomputer mirrors it on).
      if (other.id === dragged?.id) continue;
      // A map asset is no obstacle at all to the rig's own move (`decisions/0193` point 5,
      // `projects/terraformer-clears-assets.md`): the sweep that follows removes every asset its
      // band touches, so the rig may drive through a tree and park on one.
      if (mayStopOnOccupants && isMapAsset(other)) continue;
      // A live rooted object that is NOT a catalogue asset (hand-authored data) is not removed, and
      // nothing can shunt it out from under the rig, so the rig may drive through it but not park on it.
      if (mayStopOnOccupants && isObjectKind(other) && isRooted(other) && !hasStatus(other.statuses, "Destroyed")) {
        for (const cell of world.footprint(other.pos, other.base.size).cells()) noStop.add(world.key(cell));
        continue;
      }
      // The twin of the `placements()` gate — an object exits occupancy classification
      // (neither `blocked` nor `noStop`) when destroyed, an open door (via {@link isOpenDoor}),
      // or a LIVE passable object whose `objectProfile.blocksMovement` isn't `true`. The gate
      // must live HERE too, because the mover-present path routes through `classifyOccupant`
      // directly instead of `placements()`, and a destroyed object is an unconditional
      // `"blocked"` there. A passable object still contributes its `moveCost` below (it can be
      // difficult terrain) — only the blocked/noStop tier is skipped.
      if (isObjectKind(other) && (hasStatus(other.statuses, "Destroyed") || isOpenDoor(other) || other.base.objectProfile?.blocksMovement !== true)) continue;
      // `isFlyingMover` (which already folds in `opts.as`) is threaded through as
      // `classifyOccupant`'s optional 3rd argument — RAW's flight exemption covers occupants
      // (a hostile character, an object) exactly the same as it covers terrain/edges below; a
      // per-walk `as:"flying"` override that cleared walls but left occupants solid would be
      // only half of the override.
      const tier = classifyOccupant(mover, other, isFlyingMover);
      if (tier === "noStop" && mayStopOnOccupants) continue;
      const cells = world.footprint(other.pos, other.base.size).cells();
      const target = tier === "blocked" ? blocked : noStop;
      for (const cell of cells) target.add(world.key(cell));
    }
  }
  // Union the terrain-solid keys into the same `blocked` set — movement and collision flow
  // through one predicate, no parallel pass. A Flying mover routes solid terrain into `noStop`
  // instead — RAW p.63 "ignore obstructions as long as physically possible" covers terrain the
  // same as an occupant (still endpoint-blocked, never a stop). EXCEPT a **full-height** cell
  // (`blocksFlight`) — that's the 2D-faithful reading of RAW's own carve-out ("couldn't move
  // through a gap smaller than their mech"), so it stays `blocked` even for a Flying mover.
  // A Terraforming mover skips solid terrain ENTIRELY — neither `blocked` nor `noStop`, so a
  // rock outcrop is not an obstacle to it in any sense, not even an endpoint one. Unlike Flying,
  // there is no full-height carve-out (`blocksFlight` is a clearance-overhead rule about flying
  // over a gap; a ground rig demolishing at ground level isn't subject to it), so the
  // flight-blocking exception is consulted only on the flight path.
  //
  // **Terraforming terrain must NOT go into `noStop` instead.** `noStop` is read in exactly one
  // place — `footprintEndpointFits` — so routing rock there lets the rig grind THROUGH a boulder
  // while refusing to let it STOP within Size 4 of one: it can cross the rubble field it exists
  // to flatten and then has almost nowhere legal to park in it. Stopping on rock is coherent
  // because the bulldoze is a follow-up of the SAME move (`referee/moveFollowups.ts`): the sweep
  // clears every cell the footprint covered, destination footprint included, so by the time the
  // move resolves there is no solid terrain under the rig. Judging a post-bulldoze position
  // against pre-bulldoze terrain is the bug.
  //
  // Occupants are a separate, narrower exemption: the rig may end on one only under the
  // `bulldozeFollows` opt-in (the occupant loop above), never on every walk as terrain is here.
  const flightBlockingKeys = isFlyingMover ? flightBlockingTerrainKeys(state.terrain) : undefined;
  if (!isTerraformingMover) {
    for (const key of solidTerrainKeys(state.terrain)) {
      const staysBlocked = !isFlyingMover ? true : flightBlockingKeys?.has(key) === true;
      (staysBlocked ? blocked : noStop).add(key);
    }
  }
  // The object `moveCost` contribution — folded into the terrain-derived cost as
  // `max(terrainCost, objectCost)` per cell, so an object never LOWERS the cost terrain
  // already charges, only adds on top (same "worse wins" posture the sight/cover twin
  // uses for cover degrees).
  // Assets the rig is about to flatten charge it nothing (the occupant loop above says why).
  const objectCost = objectMoveCostCells(
    world,
    mayStopOnOccupants ? Object.fromEntries(Object.entries(state.combatants).filter(([, c]) => !isMapAsset(c))) : state.combatants,
  );
  return {
    blocked,
    noStop,
    inBounds: (p: Cell) => world.inBounds(p),
    // Per-cell move cost folds three "worse wins" contributions over the terrain
    // floor, none ever LOWERING it:
    //   1. the object `moveCost` footprint — a passable object (e.g. a wreck) is
    //      difficult terrain, `max(terrainCost, objectCost)`;
    //   2. the Prone crawl floor (RAW Core p.77: "PRONE characters ... count as moving
    //      in difficult terrain") — a Prone MOVER pays at
    //      LEAST the difficult-terrain floor (2), never a `+1` stack on terrain's own
    //      cost (`moveCost` is a TOTAL per-cell cost >= 1; RAW has no stacking language,
    //      so `Math.max(cost, 2)` is "at least difficult" without inventing a 3/4 on
    //      cells already authored difficult). Mover-side rider only — a merely-Slowed
    //      (non-Prone) mover, and the absent-mover fallback branch above, skip it.
    // `Math.max` composition ⇒ order-independent, and the one closure seam keeps the
    // overlay reach ({@link reachableForCombatant}) and the reducer's `move_token`
    // charge identical by construction.
    moveCost: (p: Cell) => {
      // **A labelled INFERENCE, NOT RAW.** The FLIGHT paragraph (Core p.63) grants only ignore-obstructions +
      // Prone-immunity; the DIFFICULT TERRAIN section (p.62) carries no flight carve-out,
      // and "ignore obstructions" doesn't cover a movement *cost*. Shipped anyway as a
      // defensible inference (a flyer is above ground rubble/ice, so difficult terrain
      // shouldn't slow it) — every cell costs a flat 1 for a Flying mover, short-circuiting
      // the terrain/object/Prone-crawl floor below entirely. Flagged here, not silently
      // folded into the "worse wins" chain, since it's the one rider that ISN'T RAW.
      if (isFlyingMover) return 1;
      let cost = ignoresDifficultTerrain ? 1 : terrainMoveCost(world, state.terrain, p);
      const object = ignoresDifficultTerrain ? undefined : objectCost.get(world.key(p));
      if (object !== undefined) cost = Math.max(cost, object);
      if (mover && hasStatus(mover.statuses, PRONE_STATUS)) cost = Math.max(cost, 2);
      return cost;
    },
    // Edge walls ride the already-two-arg `enterCost(to, from)` seam: a wall on the
    // `from`↔`to` boundary makes that single step impassable while every other
    // approach stays cheap. The anchor steps one cell at a time, so the edge gate is
    // the anchor's step edge. A Flying mover ignores an ORDINARY edge wall outright —
    // RAW p.63, a wall is an obstruction flight clears the same as an occupant/solid-terrain
    // cell. A **full-height** edge wall (`blocksFlight`) is the exception — it still blocks a
    // Flying mover's crossing. A Terraforming mover ignores EVERY edge wall, full-height
    // included — checked ahead of the flight branch since its exemption is the wider of the two.
    // Erasing edge terrain depends on it: the sweep can only erase edges the swept footprint
    // reaches, so a wall that still blocked the crossing could never be erased.
    blocksEdge: (to: Cell, from: Cell) =>
      isTerraformingMover
        ? false
        : isFlyingMover
          ? edgeBlocksFlight({ space: world, edges: state.edges, a: from, b: to })
          : edgeBlocksMovement({ space: world, edges: state.edges, a: from, b: to }),
    // The engagement mid-move stop: installed ONLY when there IS a mover to evaluate
    // engagement relative to, and it lacks `Disengaged` (see {@link DISENGAGED_STATUS}) OR
    // `Trampling` (see {@link TRAMPLING_STATUS} — RAW's own "ignores engagement", the SAME
    // suppression mechanism Disengaged uses, not a separate flag). Omitted entirely (rather
    // than a predicate that always returns `false`) when suppressed, so the flood/A* see
    // byte-identical "no stop" behavior — the same absent-means-off convention every other
    // optional `MovementContext` field uses. A Feinting mover ({@link FEINTING_STATUS}) ignores
    // engagement too.
    stopsAt:
      mover && !hasStatus(mover.statuses, DISENGAGED_STATUS) && !hasStatus(mover.statuses, TRAMPLING_STATUS) && !hasStatus(mover.statuses, FEINTING_STATUS)
        ? engagementStopsAt(dragged === undefined ? state : withoutCombatant(state, dragged.id), mover)
        : undefined,
    ...(dragged !== undefined && mover?.pos != null ? { endpointOk: dragEndpointOk(world, state, { mover, partner: dragged }) } : {}),
  };
}

/**
 * The grapple partner `mover` drags on its own `move_token`: placed, not rooted, and with `mover`
 * dominant ({@link grappleDominantOf}). The same conditions the rules layer's drag recomputer
 * checks before it mirrors the partner, so whatever this exempts is moved out of the way after.
 */
export function draggedPartnerOf(state: EncounterState, mover: Combatant): Combatant | undefined {
  const partnerId = grapplePartner(state, mover.id);
  if (partnerId === undefined) return undefined;
  const partner = state.combatants[partnerId];
  if (partner?.pos == null || isRooted(partner)) return undefined;
  return grappleDominantOf(state, mover.id, partnerId) === mover.id ? partner : undefined;
}

/**
 * The cable's other end `mover` tows on its own moves ({@link TOWING_STATUS}): its `partner`, placed,
 * not rooted, still tied to `mover` by a cable winch ({@link TETHERED_STATUS}, either direction), and
 * no more than twice `mover`'s Size (Lifting and Dragging: "Mechs can drag characters or objects up
 * to twice their SIZE"). `undefined` otherwise, and then the cable's tether holds as usual.
 */
export function towedPartnerOf(state: EncounterState, mover: Combatant): Combatant | undefined {
  const partnerId = mover.statuses.find((s) => s.name === TOWING_STATUS)?.partner;
  const partner = partnerId === undefined ? undefined : state.combatants[partnerId];
  if (partner?.pos == null || isRooted(partner) || partner.base.size > 2 * mover.base.size) return undefined;
  const tied = mover.statuses.some((s) => s.name === TETHERED_STATUS && s.partner === partner.id) || partner.statuses.some((s) => s.name === TETHERED_STATUS && s.partner === mover.id);
  return tied ? partner : undefined;
}

function withoutCombatant(state: EncounterState, id: string): EncounterState {
  const { [id]: _gone, ...combatants } = state.combatants;
  return { ...state, combatants };
}

/**
 * The push guard: `mover` may end with its footprint over its dragged `partner` only when the
 * partner's mirrored cell (its anchor displaced by the mover's from→anchor vector, the drag's
 * ideal) is a legal landing for it with the mover standing at that anchor. Otherwise the drag
 * could not move the partner out from under the mover. An endpoint clear of the partner is
 * always fine here; the drag clamps or restores adjacency from there (`decisions/0068`).
 */
function dragEndpointOk(world: World<Cell>, state: EncounterState, pair: { mover: Combatant; partner: Combatant }): (anchor: Cell) => boolean {
  const { mover, partner } = pair;
  const from = mover.pos!;
  const partnerKeys = new Set(world.footprint(partner.pos!, partner.base.size).cells().map((c) => world.key(c)));
  return (anchor: Cell) => {
    const overlaps = world.footprint(anchor, mover.base.size).cells().some((c) => partnerKeys.has(world.key(c)));
    if (!overlaps) return true;
    const moved = { ...mover, pos: tagCell(encounterGridMode(state), anchor) };
    const after = { ...state, combatants: { ...state.combatants, [mover.id]: moved } };
    const ctx = movementContext(world, after, partner.id);
    const ideal = world.translate(partner.pos!, from, anchor);
    return endpointFitterIn(world, ctx, partner.base.size)(ideal);
  };
}

/**
 * Flight heading legality — does a flight `move_token` segment `from → to` lie on
 * the LOCKED `heading`'s line, re-projected from `from` (THIS segment's own
 * origin, which may differ from the heading's original `origin` once the mover
 * has traveled)?
 *
 * `world.translate(from, heading.origin, heading.dest)` displaces `from` by the
 * exact SAME vector the heading's first segment walked — `translate`'s isometry
 * contract (`@lancer-vtt/space`'s `Space.translate`) guarantees this reproduces
 * the locked DIRECTION regardless of where `from` now sits. That gives a `toward`
 * point `world.linePattern` (the same square `ray` / hex `hexRay` seam every other
 * directional template — Line/Cone — rides) can snap its aim to, drawing the
 * `distance(from, to)`-length ray in that one direction; `.contains(to)` is the
 * legality answer. Grid-agnostic and **zero new geometry** — composed entirely
 * from `Space<Cell>`'s existing `translate`/`distance` and `World<Cell>`'s
 * existing `linePattern`, per the Pinned seams (`square.ts` `ray`, the hex twin
 * `linePattern`).
 *
 * Only ever consulted for a Flying mover with a heading already locked — the
 * first flight segment of an activation (or the first after a `boost` clears it)
 * has no `heading` to check against and is unconstrained beyond ordinary reach.
 */
export function flightHeadingAllows(args: { world: World<Cell>; heading: FlightHeading; from: Cell; to: Cell }): boolean {
  const { world, heading, from, to } = args;
  const toward = world.translate(from, heading.origin, heading.dest);
  const n = world.distance(from, to);
  if (n <= 0) return true; // a no-op segment is trivially on-heading (defensive; move_token already short-circuits a real no-op earlier)
  return world.linePattern(from, toward, n).contains(to);
}

/**
 * A combatant's **remaining move budget** — the *one* place the budget rule lives, read
 * by both the `move_token` reducer (the wall) and the overlay/path helpers below (the
 * affordance), so the overlay can never paint reach the reducer would reject. The budget
 * is the unit's remaining per-turn `move` points (decremented by moves it has already made
 * this turn) — but *only* when this combatant is the one acting (`turn.phase === "acting"`
 * ∧ `turn.actor === combatant.id`). A non-acting unit has **no** move budget (`0`): it
 * can't PLAY-move at all, and every real caller is gated by {@link mayPlayMove} first.
 */
export function moveBudgetFor(state: EncounterState, combatant: Combatant): number {
  const turn = state.activation.turn;
  if (turn.phase === "acting" && turn.actor === combatant.id) return turn.budget.move;
  return 0;
}

/**
 * The **voluntary-movement bar** (RAW Core pp.77-78) —
 * the *one* status-driven wall a voluntary PLAY move checks, returning the blocking
 * reason string when this combatant's carried statuses forbid it from voluntarily
 * moving AT ALL this turn, or `null` when this predicate has no objection. This is
 * orthogonal to (and folded into) {@link mayPlayMove}: that function owns the
 * *activation* half of move legality (whose turn is it), this one owns the *status*
 * half (can this carrier voluntarily move regardless of turn).
 *
 * Gates, in the order checked:
 * 1. **Immobilized** (p.78) — "cannot make any voluntary movements."
 * 2. **Stunned / Shut Down** ({@link isStunned}, the pinned Stunned ∨ Shut Down
 *    composite) — "cannot ... move ... or take any actions" (p.78).
 * 3. **Down and Out** — a pilot at 0 HP is "unconscious and Stunned" (p.77). Encoded
 *    identically to `checkActorStatusLegality`'s can't-act classification
 *    (`packages/rules/src/encounter/statusLegality.ts`: `isStunned(actor) ||
 *    hasStatus(actor.statuses, DOWN_AND_OUT_STATUS)`) so the move wall and the
 *    action wall can never disagree about the same carrier — see that predicate's
 *    doc comment for the mirrored contract.
 * 4. **Black Thumb Rodeo** — the mech idles while its pilot rides outside it (Long Rim).
 *
 * **Does NOT bar movement:** Slowed/Prone/Grappled only restrict its FORM (standard-
 * move-only, no Boost, crawl cost) — that's the catalog's `isSlowed()` Boost-wall
 * check and the crawl-cost rider in {@link movementContext}, not an outright bar.
 *
 * **Never consulted for involuntary movement** — `forced_move` folds `pos` directly
 * in `applyResolved.ts`, never entering `move_token`/`mayPlayMove` — nor for the GM
 * force-tier `place_token` (untouched by design) or a first deploy (`pos == null`,
 * exempt by construction, same as {@link moveBudgetFor}).
 *
 * **Forward contract:** any future special-move mode (a talent/system/weapon-granted
 * movement) must consult BOTH `isSlowed()` (Slowed's "no special moves") AND this
 * predicate at birth — neither has an enforcement seam of its own today.
 */
export function voluntaryMoveBar(combatant: Pick<Combatant, "statuses">): string | null {
  if (hasStatus(combatant.statuses, IMMOBILIZED_STATUS)) {
    return "Immobilized — cannot make any voluntary movements (RAW p.78)";
  }
  if (isStunned(combatant) || hasStatus(combatant.statuses, DOWN_AND_OUT_STATUS)) {
    return "Stunned (or Shut Down/Down and Out) — cannot move or take any actions (RAW p.78)";
  }
  if (hasStatus(combatant.statuses, RODEO_STATUS)) {
    return "idling in a Black Thumb Rodeo — the mech can't be controlled from outside (Long Rim)";
  }
  return null;
}

/**
 * May this combatant make its normal in-combat (PLAY) move **right now** — the
 * activation-legality half of the move rule (RAW Core p.60: *a unit makes its move
 * during its own activation*), ANDed with the status-driven {@link voluntaryMoveBar}.
 * The *one* place this lives, read by both the `move_token` reducer (the wall) and
 * the overlay/path helpers below (the affordance), so the overlay can never invite a
 * move the reducer rejects — the twin of {@link moveBudgetFor}, which owns the
 * *distance* half.
 *
 * A PLAY move is legal for **exactly the unit that is currently activating**
 * (`turn.phase === "acting" && turn.actor === combatant.id`) AND not barred by
 * {@link voluntaryMoveBar}. Every other unit is frozen until its own turn — and when
 * **nobody** is mid-turn (`turn.phase === "idle"`: a fresh fight before the first
 * activation, or the gap between turns) **no unit may PLAY-move at all**. To move a
 * unit in PLAY you first activate it; there is no "free movement when nobody's
 * active" state. Out-of-turn repositioning — board setup, a GM fudge, shuffling a
 * non-active unit — is the force-apply `place_token`'s job (EDIT mode), never
 * `move_token`: the two-command-tier split. This is the activation twin of
 * {@link moveBudgetFor}, which owns the *distance* half.
 *
 * **First placement (deploy, `pos == null`) is NOT gated by this** — like the move
 * budget, a placement is not a move; the `move_token` reducer checks this only on a
 * *real* move (a unit that already has a `pos`). The GM repositions a non-active,
 * already-placed unit out of turn via EDIT-mode `place_token` (the budget-and-
 * activation-exempt authoring path), never `move_token`.
 */
export function mayPlayMove(state: EncounterState, combatant: Combatant): boolean {
  const turn = state.activation.turn;
  return turn.phase === "acting" && turn.actor === combatant.id && voluntaryMoveBar(combatant) == null;
}

/**
 * The legal destination anchors a *placed* combatant can reach within its
 * **remaining move budget** — the read side of the same rule `move_token` enforces,
 * for the VTT to paint a movement-range overlay when the unit is selected. Keyed by
 * `world.key` (`"x,y"` square / `"q,r"` hex), each with its cheapest arrival cost;
 * the combatant's own anchor is included (cost 0). Returns `null` for an unplaced
 * combatant (nothing to reach *from*) or one whose remaining budget is `<= 0` (it
 * has exhausted its move this turn — the overlay correctly shows no reach).
 */
export function reachableForCombatant(
  state: EncounterState,
  combatant: Combatant,
): Map<string, Reachable<Cell>> | null {
  // No reach to paint for a unit that may not move this turn (it isn't its
  // activation) — the same {@link mayPlayMove} wall `move_token` enforces, so the
  // overlay never invites a move the reducer would reject (`null` = nothing shown).
  if (!mayPlayMove(state, combatant)) return null;
  const budget = moveBudgetFor(state, combatant);
  if (combatant.pos == null || budget <= 0) return null;
  const world = worldFor(state);
  // `bulldozeFollows`: this paints `move_token`'s reach, so it opts in exactly as the reducer's
  // mid-turn branch does (a Terraforming rig may end on an occupant).
  const ctx = movementContext(world, state, { exceptId: combatant.id, bulldozeFollows: true });
  const anchors = reachableAnchors({
    space: world,
    from: combatant.pos,
    size: combatant.base.size,
    budget,
    ctx,
  });
  for (const [key, r] of anchors) if (tetherBreaker(state, combatant, r.cell)) anchors.delete(key);
  return anchors;
}

/**
 * The grid cells a *placed* combatant's **footprint** can come to cover within its
 * Speed — the set the VTT paints as the movement-range overlay. The union of every
 * reachable anchor's footprint (`world.footprint(anchor, size)`), NOT the bare
 * anchors: for a Size > 1 unit the anchor is one cell of its block (the min-corner
 * for even Sizes, the centre for odd), so painting anchors alone would offset the
 * wash off the unit's body and under-cover the cells it actually occupies. Taking
 * the footprint union makes the overlay align with the token and read as "where can
 * this chassis end up", and collapses to the anchor set for Size 1. Keyed by
 * `world.key`. `null` when {@link reachableForCombatant} is (unplaced / no Speed).
 */
export function reachableCells(state: EncounterState, combatant: Combatant): Set<string> | null {
  const anchors = reachableForCombatant(state, combatant);
  if (!anchors) return null;
  const world = worldFor(state);
  const cells = new Set<string>();
  for (const r of anchors.values()) {
    for (const cell of world.footprint(r.cell, combatant.base.size).cells()) {
      cells.add(world.key(cell));
    }
  }
  return cells;
}

/**
 * The grid cells a *placed* combatant's **footprint sweeps** along the cheapest
 * legal path from its current anchor to `dest` — the set the VTT highlights while a
 * token is dragged, previewing the tiles a committed `move_token` would travel
 * through. The footprint union over the path anchors (`anchorPathTo`, A* over the
 * SAME movement context the reducer enforces), so for a Size > 1 unit the swept
 * band aligns with its body and the preview can never disagree with what a drop
 * will accept. `null` when the combatant is unplaced / has no Speed, or `dest` is
 * **not reachable within Speed** (an illegal drop has no committed path to preview).
 * Keyed by `world.key`.
 */
export function movePathCells(
  state: EncounterState,
  combatant: Combatant,
  dest: Cell,
): Set<string> | null {
  const path = movePathAnchors(state, combatant, dest);
  if (!path) return null;
  const world = worldFor(state);
  const cells = new Set<string>();
  for (const anchor of path) {
    for (const cell of world.footprint(anchor, combatant.base.size).cells()) {
      cells.add(world.key(cell));
    }
  }
  return cells;
}

/**
 * The ordered **anchor** path a *placed* combatant takes from its current anchor to
 * `dest` — the cheapest legal route (`anchorPathTo`, A* over the SAME movement
 * context the reducer enforces, biased orthogonal-over-diagonal), `from` first and
 * `dest` last. This is the sequence the VTT *animates a token along* on remote
 * clients (so a GM's move plays as a tile-by-tile travel, not a teleport) and the
 * basis {@link movePathCells} unions into the drag highlight. `null` when the
 * combatant is unplaced / has no Speed, or `dest` is **not reachable within Speed**
 * (a teleport/illegal jump has no path — the caller settles instantly instead).
 */
export function movePathAnchors(
  state: EncounterState,
  combatant: Combatant,
  dest: Cell,
): Cell[] | null {
  // No path to preview/animate for a unit that may not move this turn — the same
  // {@link mayPlayMove} wall, so the drag trail vanishes for a non-active unit
  // (and a remote move the server would never have accepted has nothing to trace).
  if (!mayPlayMove(state, combatant)) return null;
  const budget = moveBudgetFor(state, combatant);
  if (combatant.pos == null || budget <= 0) return null;
  const world = worldFor(state);
  // The `move_token` route context: the referee's committed-path capture reads this too, so the
  // bulldoze sweep receives the path to an occupied endpoint, and a Trampler's route crosses nobody.
  const ctx = defaultRouteContext(world, state, combatant);
  return anchorPathTo({
    space: world,
    from: combatant.pos,
    dest,
    size: combatant.base.size,
    budget,
    ctx,
  });
}

/**
 * The characters a move could trample: every other live, placed character (`isCharacter`, the
 * sweep's own lane; objects and deployables are never trampled).
 */
function crossableCharacters(state: EncounterState, moverId: string): Combatant[] {
  return Object.values(state.combatants).filter((c) => c.id !== moverId && isCharacter(c) && c.pos != null && !hasStatus(c.statuses, "Destroyed"));
}

/**
 * The distinct character ids (sorted) whose footprint `moverId`'s anchor `path` sweeps, `from` and
 * `to` inclusive — the Trample sweep's crossing set (`@lancer-vtt/rules`' `trampledCharacterIds`)
 * and what a Trampler's default route avoids. `[]` for a path shorter than 2 or a missing mover.
 */
export function charactersCrossed(state: EncounterState, moverId: string, path: readonly Cell[]): string[] {
  const mover = state.combatants[moverId];
  if (mover === undefined || path.length < 2) return [];
  const world = worldFor(state);
  const swept = new Set<string>();
  for (const anchor of path) for (const cell of world.footprint(anchor, mover.base.size).cells()) swept.add(world.key(cell));
  return crossableCharacters(state, moverId)
    .filter((c) => world.footprint(c.pos!, c.base.size).cells().some((cell) => swept.has(world.key(cell))))
    .map((c) => c.id)
    .sort();
}

/**
 * The context a `move_token` with no `path` routes through. For every mover but a Trampler it is
 * exactly `movementContext(…, { bulldozeFollows: true })`. A Trampler ({@link TRAMPLING_STATUS})
 * may cross characters but its default route never does (`landing-zone-full-automation` Q2): their
 * cells join `blocked`, so the cheapest route is one that crosses nobody, and a crossing has to be
 * asked for with a `path`.
 */
export function defaultRouteContext(world: World<Cell>, state: EncounterState, mover: Combatant): MovementContext<Cell> {
  const ctx = movementContext(world, state, { exceptId: mover.id, bulldozeFollows: true });
  if (!hasStatus(mover.statuses, TRAMPLING_STATUS)) return ctx;
  const blocked = new Set(ctx.blocked);
  for (const c of crossableCharacters(state, mover.id)) for (const cell of world.footprint(c.pos!, c.base.size).cells()) blocked.add(world.key(cell));
  return { ...ctx, blocked };
}

/**
 * Check a chosen anchor `path` for `mover` (Q1): it starts on the mover, each step is to a
 * neighbour its footprint can take, no engagement stop cuts it short, and it ends where the mover
 * may stop. Mid-path occupants are passed as the ordinary movement context allows (a Trampler
 * passes every character). Returns the path's own cost and whether its last step stops the mover;
 * the budget is the caller's check.
 */
export function checkMovePath(state: EncounterState, mover: Combatant, path: readonly Cell[]): Result<{ cost: number; stopped: boolean }, string> {
  const world = worldFor(state);
  if (mover.pos == null) return err("a first placement takes no path");
  if (path.length === 0 || !world.equals(path[0]!, mover.pos)) return err(`the path must start on ${mover.id}'s cell ${world.key(mover.pos)}`);
  const ctx = movementContext(world, state, { exceptId: mover.id, bulldozeFollows: true });
  const walked = movePathCost({ space: world, path, size: mover.base.size, ctx });
  if (walked.ok) return ok({ cost: walked.cost, stopped: walked.stopped });
  const at = world.key(walked.at);
  switch (walked.cause) {
    case "not_adjacent":
      return err(`the path steps to ${at}, which is not next to the cell before it`);
    case "impassable":
      return err(`the path is blocked at ${at}`);
    case "stopped_early":
      return err(`the path is stopped by engagement at ${at}, before its end`);
    case "endpoint_occupied":
      return err(`the path ends at ${at}, where ${mover.id} cannot stop`);
  }
}

/**
 * The anchor path from `mover`'s cell through each waypoint in order to `to` — the CLI's
 * `move --via`, and the route a drag through a character's token commits. Each leg is the cheapest
 * route ({@link anchorLegPath}) under the ordinary movement context, so a waypoint may be an
 * occupied cell to pass through. `null` when a leg has no route. Legality is `move_token`'s check.
 */
export function movePathThrough(state: EncounterState, mover: Combatant, stops: { via: readonly Cell[]; to: Cell }): Cell[] | null {
  if (mover.pos == null) return null;
  const world = worldFor(state);
  const ctx = movementContext(world, state, { exceptId: mover.id, bulldozeFollows: true });
  const path: Cell[] = [mover.pos];
  for (const stop of [...stops.via, stops.to]) {
    const leg = anchorLegPath({ space: world, from: path[path.length - 1]!, dest: stop, size: mover.base.size, ctx });
    if (leg === null) return null;
    path.push(...leg.slice(1));
  }
  return path;
}

/**
 * A dragged Trampler's trail after the pointer enters `dest` (`landing-zone-full-automation` Q3):
 * the anchor path from the mover's cell that the drop commits. A `dest` already on the trail cuts
 * it there. Otherwise the trail extends to `dest` along the cheapest leg that crosses no character
 * but the ones `dest` itself covers, so a pointer entering a token tramples it while a gap left by
 * a fast pointer never does, as long as that detour fits the move budget (Q2's rule); otherwise the
 * ordinary leg, which may cross. `null` when `dest` can't be reached from the trail's end at all.
 * The drop checks the whole trail against the budget.
 */
export function trampleTrailTo(state: EncounterState, mover: Combatant, step: { trail: readonly Cell[]; dest: Cell }): Cell[] | null {
  const { trail, dest } = step;
  if (mover.pos == null) return null;
  const world = worldFor(state);
  const walked = trail.length > 0 && world.equals(trail[0]!, mover.pos) ? trail : [mover.pos];
  const at = walked.findIndex((c) => world.equals(c, dest));
  if (at >= 0) return walked.slice(0, at + 1);
  const ctx = movementContext(world, state, { exceptId: mover.id, bulldozeFollows: true });
  const entered = new Set(world.footprint(dest, mover.base.size).cells().map((c) => world.key(c)));
  const avoid = new Set(ctx.blocked);
  for (const c of crossableCharacters(state, mover.id)) {
    const cells = world.footprint(c.pos!, c.base.size).cells().map((cell) => world.key(cell));
    if (!cells.some((k) => entered.has(k))) for (const k of cells) avoid.add(k);
  }
  const from = walked[walked.length - 1]!;
  const cost = (path: readonly Cell[]): number => {
    const r = movePathCost({ space: world, path, size: mover.base.size, ctx: { ...ctx, stopsAt: undefined } });
    return r.ok ? r.cost : Number.POSITIVE_INFINITY;
  };
  const around = anchorLegPath({ space: world, from, dest, size: mover.base.size, ctx: { ...ctx, blocked: avoid } });
  const extended = (leg: readonly Cell[]): Cell[] => [...walked, ...leg.slice(1)];
  if (around !== null && cost(extended(around)) <= moveBudgetFor(state, mover)) return extended(around);
  const through = anchorLegPath({ space: world, from, dest, size: mover.base.size, ctx });
  return through === null ? null : extended(through);
}

/**
 * The refusal for a Trampler's `move_token` with no `path` whose every in-budget route to `to`
 * crosses a character (Q2), naming who the cheapest one crosses. `null` for any other mover, or
 * when `to` is out of reach either way (the ordinary reject covers that).
 */
export function trampleRouteRefusal(state: EncounterState, mover: Combatant, to: Cell): string | null {
  if (mover.pos == null || !hasStatus(mover.statuses, TRAMPLING_STATUS)) return null;
  const world = worldFor(state);
  const ctx = movementContext(world, state, { exceptId: mover.id, bulldozeFollows: true });
  const crossing = anchorPathTo({ space: world, from: mover.pos, dest: to, size: mover.base.size, budget: moveBudgetFor(state, mover), ctx });
  if (crossing === null) return null;
  const crossed = charactersCrossed(state, mover.id, crossing);
  if (crossed.length === 0) return null;
  const names = crossed.map((id) => state.combatants[id]!.name).join(", ");
  const via = crossed.map((id) => world.key(state.combatants[id]!.pos!)).join(";");
  return `${mover.name}'s routes to ${world.key(to)} all cross ${names}. Drag over their token (or --via ${via}) to trample them, or pick another cell`;
}

/**
 * The cable winch's tether ({@link TETHERED_STATUS}): the other end of a cable `mover` would be more
 * than {@link TETHER_RANGE} spaces (footprint to footprint) from if it ended a voluntary move at
 * `to`, or `undefined` when no tether is stretched. `move_token` refuses such a move; the move-range
 * wash leaves those anchors out. The end `mover` tows ({@link towedPartnerOf}) is exempt: it follows.
 */
export function tetherBreaker(state: EncounterState, mover: Combatant, to: Cell): Combatant | undefined {
  const world = worldFor(state);
  const here = cellRegion(world.footprint(to, mover.base.size).cells(), world.key);
  // The end `mover` tows follows it (`dragRecompute.ts`), so its cable never stretches.
  const towed = towedPartnerOf(state, mover)?.id;
  return Object.values(state.combatants).find((other) => {
    if (other.pos == null || other.id === mover.id || other.id === towed) return false;
    const tied =
      mover.statuses.some((s) => s.name === TETHERED_STATUS && s.partner === other.id) || other.statuses.some((s) => s.name === TETHERED_STATUS && s.partner === mover.id);
    return tied && world.regionDistance(here, cellRegion(world.footprint(other.pos, other.base.size).cells(), world.key)) > TETHER_RANGE;
  });
}

/**
 * The anchors `mover` can end a granted move of `budget` spaces on ("you may move 2 spaces",
 * Lockbreaker; "move spaces equal to your Speed", Reactive Weave): the ordinary movement flood —
 * obstructions, difficult terrain, no ending on an occupant — with no engagement stop unless
 * `engagementStops` (most such grants say "This movement ignores engagement"). No turn or
 * move-budget gate: the grant is its own budget, and it can come off-turn (a Brace). The mover's own
 * anchor is one. Unplaced mover → none.
 */
export function grantedMoveLandings(state: EncounterState, mover: Combatant, move: { budget: number; engagementStops?: boolean }): Cell[] {
  if (mover.pos == null) return [];
  const world = worldFor(state);
  const ctx = movementContext(world, state, mover.id);
  const anchors = reachableAnchors({ space: world, from: mover.pos, size: mover.base.size, budget: move.budget, ctx: move.engagementStops ? ctx : { ...ctx, stopsAt: undefined } });
  return [...anchors.values()].map((r) => r.cell);
}

/**
 * The legal **teleport** destination anchors for a *placed* combatant carrying a frozen
 * `base.teleport` capability — the domain-side read the `teleport_token` reducer validates
 * against, and the seam a future canvas picker paints from. **Not a flood** (unlike
 * {@link reachableForCombatant}): teleport has no path, so every cell within the frozen
 * range is checked independently, not walked to.
 *
 * A candidate anchor qualifies iff its footprint-to-footprint distance from the
 * combatant's current footprint ({@link "@lancer-vtt/space".World.regionDistance}, the
 * same measure `teleport_token`'s range wall enforces) is `<= range` AND the destination
 * is free ({@link "@lancer-vtt/space".footprintEndpointFits} — `blocked ∪ noStop`, so an
 * occupied cell never appears, matching the reducer's own endpoint gate exactly). No
 * turn/activation/budget gating here — unlike {@link reachableForCombatant}, this answers
 * "where COULD this unit teleport if it were legal to right now," the geometry-only half;
 * the reducer is still the authority on WHEN.
 *
 * `null` for an unplaced combatant or one with no `base.teleport` capability — nothing to
 * measure from / nothing to gate. The search radius is generously bounded
 * (`range + ceil(size)`, mirroring `dragRecompute.ts`'s `restoreAdjacency` bound) so a
 * Size > 1 mover's footprint-to-footprint distance is never under-searched by the
 * center-to-center `disk`.
 */
export function teleportTargets(state: EncounterState, combatant: Combatant): Set<string> | null {
  const range = combatant.base.teleport?.range;
  if (combatant.pos == null || range === undefined) return null;
  const world = worldFor(state);
  return new Set(teleportLandings({ state, mover: combatant, range }).map((c) => world.key(c)));
}

/**
 * The free anchors `mover` can teleport to within `range` of `from` — the geometry under
 * {@link teleportTargets} and the `teleport` effect atom. `from` is the region the range is measured
 * from: omitted, the mover's own footprint; a Blink Mine measures from its own space, a Warp
 * Grenade from its blast center. A candidate qualifies iff its footprint is within `range` of
 * `from` (footprint-to-footprint) and {@link placementFitter} admits it, so the mover's own anchor
 * is one. Unplaced mover → none.
 */
export function teleportLandings(args: { state: EncounterState; mover: Combatant; range: number; from?: Cell[] }): Cell[] {
  const { state, mover, range } = args;
  if (mover.pos == null) return [];
  const world = worldFor(state);
  const fits = placementFitter(state, { mover: mover.id, size: mover.base.size });
  const fromCells = args.from ?? world.footprint(mover.pos, mover.base.size).cells();
  const fromFootprint = cellRegion(fromCells, world.key);
  const center = args.from?.[0] ?? mover.pos;
  const searchRadius = range + Math.ceil(mover.base.size);
  const out: Cell[] = [];
  for (const candidate of world.disk(center, searchRadius).cells()) {
    const toFootprint = cellRegion(world.footprint(candidate, mover.base.size).cells(), world.key);
    if (world.regionDistance(fromFootprint, toFootprint) > range) continue;
    if (!fits(candidate)) continue;
    out.push(candidate);
  }
  return out;
}
